import { Stack, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { withLDConsumer } from "launchdarkly-react-client-sdk";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import { WorkspacesContext } from "../../contexts/WorkspacesContext";
import useWorkspaces from "../../hooks/useWorkspaces";
import { getWorkspaceUserMembershipsFunc } from "../../lib/helper";
import { scrollbarStyle } from "../../shared/dashboard";
import { WorkspaceUserMembership } from "../../types/files";
import WorkspacesTableHeader, { InvitationTableHeaders } from "../molecules/workspaces-table/WorkspacesTableHeader";
import WorkspacesTableRow from "../molecules/workspaces-table/WorkspacesTableRow";
import { collectionKey } from "../pages/InvestorsPage";

const useStyles = makeStyles((theme) => ({
    title: {
        fontFamily: 'Inter',
        fontSize: '1.2rem',
        fontWeight: 600,
        color: theme.colors.neutral['500'],
        textAlign: 'left',
    },
    table: {
        width: '100%',
        height: 'fit-content',
        minHeight: '126px',
        maxHeight: '246px',
        overflow: 'hidden',
    },
    headers: {
        width: '100%',
        height: '100%',
        overflowX: 'auto',
        overflowY: 'hidden',
        ...scrollbarStyle,
    },
    rows: {
        minWidth: '100%',
        width: 'fit-content',
        height: '100%',
        overflowX: 'hidden',
        overflowY: 'auto',
        ...scrollbarStyle,
    },
}));
const SharedWorkspaceInvite: React.FC<{
    flags?: any,
    onInviteCount?: (count: number) => void,
 }> = ({flags, onInviteCount}) => {
    const classes = useStyles();
    const { userGroup } = useContext(AuthContext);
    const { pendingWorkspaces } = useContext(WorkspacesContext);
    const {fetchWorkspaceUserMemberships} = useWorkspaces();
    const [investorCounts, setInvestorCounts] = useState<number[]>([]);

    const handleInvite = useCallback(async () => {
        await fetchWorkspaceUserMemberships();
    }, [fetchWorkspaceUserMemberships]);

    useEffect(() => {
        if (!!userGroup)
            (async () => await fetchWorkspaceUserMemberships())();

        return () => {
            fetchWorkspaceUserMemberships();
        }
        // eslint-disable-next-line
    }, [userGroup]);

    useEffect(() => {
        if (!!pendingWorkspaces.length) {
            Promise.all(pendingWorkspaces.map(ws => getWorkspaceUserMembershipsFunc({workspaceId: ws.id})))
                .then(memberships => {
                    setInvestorCounts(memberships.map(membership => {
                        const members = membership as {items: WorkspaceUserMembership[]};

                        return members?.items?.length ?? 0;
                    }));
                });
            onInviteCount?.(pendingWorkspaces.length);
        }
    // eslint-disable-next-line
    }, [pendingWorkspaces]);

    if (!pendingWorkspaces.length)
        return (<></>);

    return (<>
        <Stack width="100%" height="fit-content" my={2}>
            <Typography className={classes.title}>
                {'Invites to deal sharing workspaces '}
                <Typography component="span" fontFamily="Inter" fontSize="1.1rem" fontWeight="500" display="inline">
                    {`(${pendingWorkspaces.length ?? 0})`}
                </Typography>
            </Typography>
            <Stack className={classes.table}>
                <Stack className={classes.headers} alignItems="flex-start" justifyContent="flex-start">
                    <WorkspacesTableHeader columns={InvitationTableHeaders} />
                    <Stack className={classes.rows} alignItems="flex-start" justifyContent="flex-start">
                        {pendingWorkspaces.map((workspace, i) => (
                            <WorkspacesTableRow
                                workspace={workspace}
                                columns={InvitationTableHeaders}
                                collectionKey={collectionKey}
                                investors={investorCounts[i]}
                                onInvite={handleInvite}
                                noMenu overview invite
                                key={'shared-workspace-invite-125-' + workspace.id} />
                        ))}
                    </Stack>
                </Stack>
            </Stack>
        </Stack>
    </>);
}

export default withLDConsumer()(SharedWorkspaceInvite);